/*
 * Sticky add-to-cart bar for product pages.
 * Appears at the bottom of the screen once the main add-to-cart button has
 * scrolled out of view (above the viewport, or hidden behind the sticky header).
 * Clicking it submits the real product form, so the chosen variant and quantity
 * are used and the cart drawer opens exactly like it does for the main button.
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.info = document.querySelector('product-info');
        this.form = (this.info || document).querySelector('form[data-type="add-to-cart-form"]');
        this.mainButton = this.form && this.form.querySelector('[type="submit"]');
        if (!this.form || !this.mainButton) return;

        // Remove a stale copy (theme editor re-renders), then move to <body>
        // so no transformed ancestor can break position: fixed.
        document.querySelectorAll('sticky-add-to-cart').forEach((el) => {
          if (el !== this) el.remove();
        });
        document.body.appendChild(this);

        this.button = this.querySelector('.sticky-atc__button');
        this.labelEl = this.querySelector('.sticky-atc__label');
        this.priceEl = this.querySelector('.sticky-atc__price');
        this.variantEl = this.querySelector('.sticky-atc__variant');

        this.button.addEventListener('click', () => this.submit());

        this.pastButton = false;
        this.observe();
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.observe(), 200);
        });

        // Keep label / price / variant / state in sync with the main form.
        const root = this.info || this.form.closest('section') || document.body;
        this.mutations = new MutationObserver(() => {
          cancelAnimationFrame(this.syncFrame);
          this.syncFrame = requestAnimationFrame(() => this.sync());
        });
        this.mutations.observe(root, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled', 'checked', 'value', 'src'],
        });
        document.addEventListener('change', () => setTimeout(() => this.sync(), 50));
        this.sync();
      }

      // The main button counts as "out of view" once it scrolls above the
      // viewport or slides under the sticky header.
      observe() {
        if (this.observer) this.observer.disconnect();
        const header = document.querySelector('.section-header, header.header');
        const headerBottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
        this.headerOffset = headerBottom;
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.pastButton = !entry.isIntersecting && entry.boundingClientRect.top < this.headerOffset + 1;
            this.toggle();
          },
          { threshold: 0, rootMargin: '-' + headerBottom + 'px 0px 0px 0px' }
        );
        this.observer.observe(this.mainButton);
      }

      toggle() {
        const show = this.pastButton;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.button.tabIndex = show ? 0 : -1;
        document.body.style.paddingBottom = show ? this.offsetHeight + 'px' : '';
      }

      submit() {
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        if (this.mainButton.disabled) return;
        if (this.form.requestSubmit) this.form.requestSubmit(this.mainButton);
        else this.mainButton.click();
      }

      sync() {
        // Button label + state
        const span = this.mainButton.querySelector('span');
        const label = span ? span.textContent.trim() : this.mainButton.textContent.trim();
        if (label && this.labelEl.textContent !== label) this.labelEl.textContent = label;
        const busy =
          this.mainButton.disabled ||
          this.mainButton.getAttribute('aria-disabled') === 'true' ||
          this.mainButton.classList.contains('loading');
        this.button.setAttribute('aria-disabled', busy ? 'true' : 'false');

        // Price
        try {
          const container = this.info && this.info.querySelector('[id^="price-"] .price');
          if (container && this.priceEl) {
            const now = container.querySelector('.price-item--last');
            const current = now ? now.textContent.trim() : '';
            let was = '';
            if (container.classList.contains('price--on-sale')) {
              const w = container.querySelector('.price__sale .price-item--regular');
              was = w ? w.textContent.trim() : '';
            }
            const key = current + '|' + was;
            if (current && this.priceKey !== key) {
              this.priceKey = key;
              this.priceEl.textContent = current;
              if (was) {
                const s = document.createElement('s');
                s.textContent = was;
                this.priceEl.appendChild(s);
              }
            }
          }
        } catch (e) {}

        // Selected options (e.g. "Black / Large")
        try {
          if (this.variantEl) {
            const picker = document.querySelector('variant-selects, variant-radios');
            const parts = [];
            if (picker) {
              picker.querySelectorAll('select').forEach((s) => {
                const o = s.options[s.selectedIndex];
                if (o) parts.push(o.textContent.trim());
              });
              picker.querySelectorAll('input[type="radio"]:checked').forEach((r) => parts.push(r.value));
            }
            const text = parts.join(' / ');
            if (this.variantEl.textContent !== text) this.variantEl.textContent = text;
            this.variantEl.hidden = !text || text === 'Default Title';
          }
        } catch (e) {}
      }
    }
  );
}
